import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { RequestBuilderService } from 'src/app/components/requestbuilder/request-builder.service';
import { SignatureProviderTypeEnum } from 'src/app/constants/signature-provider-type.enum';
import { BuilderActorModel } from 'src/app/models/builder/builder-actor.model';
import { Provider } from 'src/app/models/signatureProvider';
import { RequestStatusService } from '../requeststatus.service';
import { StatusActorModel } from 'src/app/models/status/status-actor.model';
import { ActorSendGroupStatusInformationModel, ActorStatusInformationModel } from 'src/app/models/api/actor-status-information.model';
import { SingleOrDefault } from 'src/app/_helpers/ArrayExtensionMethodHelper.functions';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { User } from 'src/app/models/user';

export const EditSignatureDataModalOptions: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  centered: true,
  size: 'md'
};

@Component({
    templateUrl: './edit-signature-data-dialog.modal.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditSignatureDataDialogComponent implements OnInit, OnDestroy {
  signatureProviders: Provider[] = [];
  selectedSignatureProviders: Provider[] = [];
  private initialSignatureProviders: Provider[] = [];
  disableSignatureProviders: boolean = false;
  private actor!: ActorStatusInformationModel | undefined;
  private user: User | null = null;

  @Input() actorId!: string;
  @Output() saveActor: EventEmitter<ActorSendGroupStatusInformationModel> = new EventEmitter<ActorSendGroupStatusInformationModel>();

  private signatureProviderSubscription: Subscription | null = null;
  private currentActorSubscription: Subscription | null = null;
  private currentUserSubscription: Subscription | null = null;

  constructor(private activeModal: NgbActiveModal, 
              private requestStatusService: RequestStatusService,
              private authenticationService: AuthenticationService) {
    this.signatureProviderSubscription = this.requestStatusService.signatureProviders$.subscribe((providers: Provider[]) => {
      this.signatureProviders = providers;
    });
    this.currentUserSubscription = this.authenticationService.currentUser.subscribe((user: User) => {
      this.user = user;
    });
  }

  ngOnInit(): void {
    this.selectedSignatureProviders = [];
    this.currentActorSubscription = this.requestStatusService.sendgroups$.subscribe((sendgroups: ActorSendGroupStatusInformationModel[]) => {
      this.actor = sendgroups
        .map(group => group.actors)
        .flat()
        .find(actor => actor && actor.inviteId === this.actorId);
      this.actor?.allowedQtsps?.forEach(qtsp => {
        this.selectedSignatureProviders.push(...this.signatureProviders.filter(s => s.id.endsWith(qtsp)));
      });
      this.initialSignatureProviders = [...this.selectedSignatureProviders];

      /*
        TODO: We need to check based on the current Actor which signature providers 
        and is a user with signatureprovider we need to give to them.
      */

      // Auto-select signature provider if actor is the current user, using custom logic    
      // if (this.user?.email == this.actor?.email && this.user?.phoneNumber == this.actor?.phone) {
      //   for (const provider of this.signatureProviders) {
      //     if (!this.user?.cscProviderName && !this.user?.signatureProvider && provider.signatureProviderType === SignatureProviderTypeEnum.Eseal) {
      //       if (!this.selectedSignatureProviders.some(s => s.id === provider.id)) {
      //         this.selectedSignatureProviders.push(provider);
      //       }
      //     } else if (!this.user?.cscProviderName && this.user?.signatureProvider &&
      //       (provider.signatureProviderType === SignatureProviderTypeEnum.TokenCertificateSignatureProvider ||
      //         provider.signatureProviderType === SignatureProviderTypeEnum.HardTokenAgentSignatureProvider)) {
      //       if (!this.selectedSignatureProviders.some(s => s.id === provider.id)) {
      //         this.selectedSignatureProviders.push(provider);
      //       }
      //     } else if (this.user?.cscProviderName && this.user?.signatureProvider) {
      //       if (provider.id.endsWith(this.user.cscProviderName)) {
      //         if (!this.selectedSignatureProviders.some(s => s.id === provider.id)) {
      //           this.selectedSignatureProviders.push(provider);
      //         }
      //       }
      //     }
      //   }
      // }
    });
  }

  ngOnDestroy(): void {
    if (this.currentActorSubscription) {
      this.currentActorSubscription.unsubscribe();
    }
    if (this.signatureProviderSubscription) {
      this.signatureProviderSubscription.unsubscribe();
    }
    if (this.currentUserSubscription) {
      this.currentUserSubscription.unsubscribe();
    }
  }

  public close() {
    this.activeModal.close();
  }

  public save() {
    if (this.actor === null || this.actor === undefined) {
      this.close();
      return;
    }
    if (this.selectedSignatureProviders.length > 0) {
      this.actor!.allowedQtsps = [];
      this.selectedSignatureProviders.forEach(qtsp => {
        this.actor!.allowedQtsps!.push(this.getProviderValue(qtsp))
      });
    }
    this.activeModal.close(this.actor);
  }

  getProviderValue(qtsp: Provider) {
    return this.requestStatusService.getProviderValue(qtsp);
  }

  public getSelectedSignatureProviders() {
    return this.selectedSignatureProviders.map(qtsp => qtsp.name).join(', ');
  }

  public applySignatureProvicerSelection(event: any) {
    let isChecked: boolean = (event.srcElement as HTMLInputElement).checked;
    let qtspId: string = (event.target as HTMLInputElement).value;
    if (qtspId.toLocaleLowerCase() === "token") qtspId = SignatureProviderTypeEnum.TokenCertificateSignatureProvider;
    if (isChecked && this.selectedSignatureProviders.findIndex(s => s.id.endsWith(qtspId)) < 0) {
      let qtspIndex = this.signatureProviders.findIndex(s => s.id.endsWith(qtspId));
      this.selectedSignatureProviders.push(this.signatureProviders[qtspIndex]);
    } else {
      let qtspIndex = this.selectedSignatureProviders.findIndex(s => s.id.endsWith(qtspId));
      this.selectedSignatureProviders.splice(qtspIndex, 1);
    }
  }

  public isDisabled(provider: Provider) {
    if(this.actor?.allowedQtsps?.length === 0) {
      return false;
    }

    if(this.requestStatusService.workflowData) {

      let step3 = this.requestStatusService.workflowData.step3;
      if(!step3.allowsESeal && provider.signatureProviderType === SignatureProviderTypeEnum.Eseal){
        return true;
      }
      if(step3.allowedQtsps?.length ?? 0 > 0){
        return !step3.allowedQtsps?.includes(this.getProviderValue(provider));
      }
      if((step3.placeholders?.length ?? 0) > 0 && this.actor?.workflowPlaceholderId != null){
        let placeholderForActor = SingleOrDefault(step3.placeholders ?? [], "defaultPersonPlaceholderId", this.actor.workflowPlaceholderId);
        if(placeholderForActor != null && (placeholderForActor.allowedQtsps?.length ?? 0) > 0){
          return true;
        }
      }
    }

    /*
      TODO: We need to check based on the current Actor which signature providers 
      and is a user with signatureprovider we need to give to them.
    */

    // if(this.user != null && this.user.email === this.actor?.email && this.user.phoneNumber === this.actor?.phone){
    //   if(!this.user.cscProviderName && 
    //      !this.user.signatureProvider && 
    //      provider.signatureProviderType === SignatureProviderTypeEnum.Eseal)
    //   {
    //     return true;
    //   }
    //   else if(!this.user.cscProviderName && 
    //           this.user.signatureProvider && 
    //           (provider.signatureProviderType === SignatureProviderTypeEnum.TokenCertificateSignatureProvider ||
    //            provider.signatureProviderType === SignatureProviderTypeEnum.HardTokenAgentSignatureProvider)
    //          )
    //   {
    //     return true;
    //   }
    //   else if(this.user.cscProviderName && 
    //           this.user.signatureProvider)
    //   {
    //     return provider.id.endsWith(this.user.cscProviderName)
    //   }
    // }

    return false;
  }

  public checkIfProviderIsSelected(provider: Provider) {
    return this.selectedSignatureProviders.includes(provider);
  }

  get hasSelectionChanged(): boolean {
    if (this.selectedSignatureProviders.length !== this.initialSignatureProviders.length) return true;
    return this.selectedSignatureProviders.some(p => !this.initialSignatureProviders.includes(p));
  }

  get dropdownDisabled(): boolean {
    return this.signatureProviders.filter(p => !this.disableSignatureProviders && !this.isDisabled(p)).length < 2;
  }
}
